import { Box, Button, Flex, Grid, Input, Text } from "@chakra-ui/react";
import {
  ConsultaPropsNewInterface,
  ContratoCartaoRMCNewInterface,
  ContratoEmprestimoNewInterface,
} from "./types";
import { currencyMask, maskCPF, maskPhoneFormat } from "utils/string-formats";
import DynamicTable from "components/dynamic-table";
import { ViewIcon } from "@chakra-ui/icons";
import { FaFilePdf } from "react-icons/fa";
import { ConsultaPDFModal, openConsultaPDFModal } from "../consulta-pdf-modal";
import {
  ConsultaDetalhamentoModal,
  openConsultaDetalhamentoModal,
} from "../consulta-detalhamento-modal";
import { parseCodBancoToEnumBanco } from "components/atendimentos-components/atendimento-form/contracts-table/consts";
import { dropdownEspecies } from "components/atendimentos-components/atendimento-form/fields-data";

export function ConsultaFields({
  consult,
}: {
  consult: ConsultaPropsNewInterface;
}) {
  const completeCPF = (cpf: string) => {
    if (cpf != null && cpf)
      for (let i = 0; i <= 11; i++) if (i > cpf.length) cpf = "0" + cpf;
    return maskCPF(cpf);
  };

  const completeNB = (nb: string) => {
    if (nb != null && nb)
      for (let i = 0; i <= 10; i++) if (i > nb.length) nb = "0" + nb;
    return nb;
  };

  const dateFormat = (data: string, withOutYears = false) => {
    const today = new Date();
    const birthDate = new Date(data);
    let age = today.getFullYear() - birthDate.getUTCFullYear();
    const month = today.getMonth() - birthDate.getUTCMonth();
    const day = today.getDate() - birthDate.getUTCDate();

    if (month < 0) age -= 1;
    else if (month === 0 && day < 0) age -= 1;

    return `${data?.split("-").reverse().join("/") ?? ""}${
      withOutYears ? "" : ` (${age} anos)`
    }`;
  };

  const addressColumns = [
    {
      name: "UF",
      render: (row: ConsultaPropsNewInterface["dadosCadastrais"]) => row.uf,
    },
    {
      name: "Cidade",
      render: (row: ConsultaPropsNewInterface["dadosCadastrais"]) => row.cidade,
    },
    {
      name: "Bairro",
      render: (row: ConsultaPropsNewInterface["dadosCadastrais"]) => row.bairro,
    },
    {
      name: "Endereco",
      render: (row: ConsultaPropsNewInterface["dadosCadastrais"]) =>
        row.endereco,
    },
    {
      name: "CEP",
      render: (row: ConsultaPropsNewInterface["dadosCadastrais"]) => row.cep,
    },
  ];

  const contratoCartaoColumns = [
    {
      name: "Banco",
      render: (row: ContratoCartaoRMCNewInterface) =>
        `${row.banco} - ${row.nomeBanco}`,
    },
    {
      name: "Contrato",
      render: (row: ContratoCartaoRMCNewInterface) => row.contrato,
    },
    // {
    //   name: "Tipo de empréstimo",
    //   render: (row: ContratoCartaoRMCNewInterface) => row.tipoEmprestimo.descricao,
    // },
    {
      name: "Limite Cartão",
      render: (row: ContratoCartaoRMCNewInterface) =>
        typeof row.valor === "number" ? (
          <Text color="green.400">R$ {currencyMask(row.valor.toFixed(2))}</Text>
        ) : null,
    },
    {
      name: "Início contrato",
      render: (row: ContratoCartaoRMCNewInterface) =>
        row.dataInclusao ? dateFormat(row.dataInclusao, true) : null,
    },
    {
      name: "Valor Reservado",
      render: (row: ContratoCartaoRMCNewInterface) =>
        typeof row.valorParcela === "number" ? (
          <Text color="green.400">
            R$ {currencyMask(row.valorParcela.toFixed(2))}
          </Text>
        ) : null,
    },
    // {
    //   name: "Situação",
    //   render: (row: ContratoCartaoRMCNewInterface) =>
    //     row.?.toLocaleLowerCase() === "ativo" ? (
    //       <Text color="green.400">{row.situacao}</Text>
    //     ) : (
    //       row.situacao
    //     ),
    // },
  ];

  const contratosBancariosColumns = [
    {
      name: "Banco",
      render: (row: ContratoEmprestimoNewInterface) =>
        `${row.banco} - ${row.nomeBanco}`,
    },
    {
      name: "Contrato",
      render: (row: ContratoEmprestimoNewInterface) => row.contrato,
    },
    // {
    //   name: "Tipo de Empréstimo",
    //   render: (row: ContratoEmprestimoNewInterface) =>
    //     `${row.} - ${row.tipoEmprestimo.descricao}`,
    // },
    {
      name: "Valor Empréstimo",
      render: (row: ContratoEmprestimoNewInterface) =>
        typeof row.valorEmprestimo === "number" ? (
          <Text>R$ {currencyMask(row.valorEmprestimo.toFixed(2))}</Text>
        ) : null,
    },
    {
      name: "Início Desconto",
      render: (row: ContratoEmprestimoNewInterface) =>
        row.dataInicioDesconto
          ? dateFormat(row.dataInicioDesconto, true)
          : null,
    },
    {
      name: "Fim Desconto",
      render: (row: ContratoEmprestimoNewInterface) =>
        row.dataFimDesconto ? dateFormat(row.dataFimDesconto, true) : null,
    },
    {
      name: "Em aberto parcelas",
      render: (row: ContratoEmprestimoNewInterface) => (
        <Text>
          {row.parcelasRestantes}/{row.prazo}
        </Text>
      ),
    },
    {
      name: "Valor da parcela",
      render: (row: ContratoEmprestimoNewInterface) =>
        typeof row.valorParcela === "number" ? (
          <Text>R$ {currencyMask(row.valorParcela.toFixed(2))}</Text>
        ) : null,
    },
    {
      name: "Data Averbação",
      render: (row: ContratoEmprestimoNewInterface) =>
        row.dataAverbacao ? dateFormat(row.dataAverbacao, true) : null,
    },
    // {
    //   name: "Situação",
    //   render: (row: ContratoEmprestimoNewInterface) =>
    //     row.?.toLocaleLowerCase() === "ativo" ? (
    //       <Text color="green.400">{row.situacao}</Text>
    //     ) : (
    //       row.situacao
    //     ),
    // },
    {
      name: "Taxa",
      render: (row: ContratoEmprestimoNewInterface) => (
        <Text>{Number(row.taxa).toFixed(2)}%</Text>
      ),
    },
    // {
    //   name: "Taxa Calculada",
    //   render: (row: ContratoEmprestimoNewInterface) => (
    //     <Text>{Number(row.t).toFixed(2)}%</Text>
    //   ),
    // },
    {
      name: "Saldo Dev. Aprox.",
      render: (row: ContratoEmprestimoNewInterface) => (
        <Text>R$ {currencyMask(row.saldoDevedor.toFixed(2))}</Text>
      ),
    },
    // {
    //   name: "Ação",
    //   render: (row: EmprestimoBancario) => (
    //     <IconButton
    //       aria-label=""
    //       variant="outline"
    //       icon={<AddIcon w="12px" h="12px" />}
    //     />
    //   ),
    // },
  ];

  return (
    <Box>
      <Grid
        borderRadius={"8"}
        mb="15px"
        p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
        bg="#fff"
        templateColumns="1fr 1fr 1fr"
        gap="16px"
        w="100%"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gridArea="auto / span 3"
        >
          <Text fontWeight="semibold" fontSize="20">
            Dados Cadastrais
          </Text>
          <Flex alignItems="center">
            <Button
              leftIcon={<ViewIcon />}
              onClick={() => openConsultaDetalhamentoModal()}
            >
              Detalhamento
            </Button>
            <Button
              ml="8px"
              leftIcon={<FaFilePdf size="18px" />}
              onClick={() => openConsultaPDFModal()}
            >
              PDF
            </Button>
          </Flex>
        </Flex>
        <Box
          gridArea="auto / span 3"
          borderBottom="1px solid var(--chakra-colors-gray-200)"
        ></Box>
        <Box>
          <Text mb="8px">Benefício:</Text>
          <Input value={completeNB(consult?.dadosCadastrais.nb)} isReadOnly />
        </Box>
        <Box>
          <Text mb="8px">Nome:</Text>
          <Input value={consult?.dadosCadastrais.nome} isReadOnly />
        </Box>
        <Box>
          <Text mb="8px">Sexo:</Text>
          <Input value={consult?.dadosCadastrais.sexo} isReadOnly />
        </Box>
        <Box>
          <Text mb="8px">CPF:</Text>
          <Input value={completeCPF(consult?.dadosCadastrais.cpf)} isReadOnly />
        </Box>
        <Box>
          <Text mb="8px">RG:</Text>
          <Input value={consult?.dadosCadastrais.rg} isReadOnly />
        </Box>
        <Box>
          <Text mb="8px">Data Nascimento:</Text>
          <Input
            value={dateFormat(consult?.dadosCadastrais.dataNascimento)}
            isReadOnly
          />
        </Box>

        <Grid templateColumns="1fr 1fr" gridArea="auto / span 3" gap="16px">
          <Box>
            <Text mb="8px">DDB:</Text>
            <Input
              value={dateFormat(consult?.dadosCadastrais.ddb, true)}
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">Nome Mãe:</Text>
            <Input value={consult?.dadosCadastrais.nomeMae} isReadOnly />
          </Box>
          <Box>
            <Text mb="8px">Espécie:</Text>
            <Input
              value={
                dropdownEspecies.find(
                  ({ value }) =>
                    consult?.dadosCadastrais?.especie === `${value}`
                )?.name
              }
              isReadOnly
            />
          </Box>
          {/* <Box>
            <Text mb="8px">NIT:</Text>
            <Input value={consult?.dadosCadastrais?.nit} isReadOnly />
          </Box> */}
          <Box>
            <Text mb="8px">Situação:</Text>
            <Input value={consult?.dadosCadastrais?.situacao} isReadOnly />
          </Box>
          <Box>
            <Text mb="8px">É pensão alimentícia:</Text>
            <Input
              value={
                consult?.dadosCadastrais?.pensaoAlimenticia != null
                  ? consult?.dadosCadastrais?.pensaoAlimenticia
                    ? "SIM"
                    : "NÃO"
                  : ""
              }
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">Permite Empréstimo:</Text>
            <Input
              value={
                consult?.dadosCadastrais.permiteEmprestimo != null
                  ? consult?.dadosCadastrais.permiteEmprestimo
                    ? "SIM"
                    : "NÃO"
                  : ""
              }
              isReadOnly
            />
          </Box>
        </Grid>
        <Grid templateColumns="1fr 1fr 1fr" gridArea="auto / span 3" gap="16px">
          <Box>
            <Text mb="8px">DIB:</Text>
            <Input
              value={dateFormat(consult?.dadosCadastrais.dib, true)}
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">Bloqueado Empréstimo:</Text>
            <Input
              value={
                consult?.dadosCadastrais?.bloqueadoEmprestimo != null
                  ? consult?.dadosCadastrais?.bloqueadoEmprestimo
                    ? "SIM"
                    : "NÃO"
                  : ""
              }
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">Possui representante legal:</Text>
            <Input
              value={
                consult?.dadosCadastrais?.representanteLegal != null
                  ? consult?.dadosCadastrais?.representanteLegal
                    ? "SIM"
                    : "NÃO"
                  : ""
              }
              isReadOnly
            />
          </Box>
        </Grid>
        {consult?.dadosCadastrais?.representanteLegal ? (
          <Grid templateColumns="1fr 1fr" gridArea="auto / span 3" gap="16px">
            <Box>
              <Text mb="8px">CPF representante legal:</Text>
              <Input
                value={
                  consult?.dadosRepresentanteLegal?.cpf
                    ? completeCPF(consult?.dadosRepresentanteLegal?.cpf)
                    : ""
                }
                isReadOnly
              />
            </Box>
            <Box>
              <Text mb="8px">Nome representante legal:</Text>
              <Input
                value={consult?.dadosRepresentanteLegal?.nome}
                isReadOnly
              />
            </Box>
          </Grid>
        ) : null}
      </Grid>
      {consult?.telefones.length && (
        <Grid
          borderRadius={"8"}
          mb="15px"
          p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
          bg="#fff"
          templateColumns="1fr 1fr 1fr"
          gap="16px"
          w="100%"
        >
          <Box gridArea="auto / span 3">
            <Text fontWeight="semibold" fontSize="20">
              Telefones
            </Text>
          </Box>
          <Box
            gridArea="auto / span 3"
            borderBottom="1px solid var(--chakra-colors-gray-200)"
          />

          {consult?.telefones.map((telefone, index) => (
            <Grid>
              <Box>
                <Text mb="8px">Telefone {index + 1}:</Text>
                <Input
                  key={telefone.numero}
                  value={telefone?.numero?.length === 11 ? maskPhoneFormat(telefone.numero).formatedValue : telefone.numero}
                  isReadOnly
                />
              </Box>
            </Grid>
          ))}
          <Box gridArea="auto / span 3">

          </Box>
        </Grid>
      )}
      <Grid
        borderRadius={"8"}
        mb="15px"
        p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
        bg="#fff"
        templateColumns="1fr 1fr 1fr"
        gap="16px"
        w="100%"
      >
        <Box gridArea="auto / span 3">
          <Text fontWeight="semibold" fontSize="20">
            Endereços do Cliente
          </Text>
        </Box>
        <Box
          gridArea="auto / span 3"
          borderBottom="1px solid var(--chakra-colors-gray-200)"
        ></Box>

        <Box gridArea="auto / span 3">
          <DynamicTable
            columns={addressColumns}
            rows={[consult?.dadosCadastrais]}
            containerStyle={{
              boxShadow: "0 0 1em rgba(0,0,0, 0.03)",
            }}
          />
        </Box>
      </Grid>
      <Grid
        borderRadius={"8"}
        mb="15px"
        p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
        bg="#fff"
        templateColumns="1fr 1fr 1fr"
        gap="16px"
        w="100%"
      >
        <Box gridArea="auto / span 3">
          <Text fontWeight="semibold" fontSize="20">
            Dados Bancários
          </Text>
        </Box>
        <Box
          gridArea="auto / span 3"
          borderBottom="1px solid var(--chakra-colors-gray-200)"
        ></Box>
        <Grid
          templateColumns="1fr 1fr 1fr 1fr"
          gap="16px"
          gridArea="auto / span 3"
        >
          <Box>
            <Text mb="8px">Banco:</Text>
            <Input
              value={`${consult?.dadosBancario.banco ?? ""} - ${
                parseCodBancoToEnumBanco(consult?.dadosBancario.banco) ?? ""
              }`}
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">Agencia:</Text>
            <Input value={consult?.dadosBancario.agenciaPgto} isReadOnly />
          </Box>
          <Box>
            <Text mb="8px">Conta:</Text>
            <Input value={consult?.dadosBancario.contaPgto ?? ""} isReadOnly />
          </Box>
          <Box>
            <Text mb="8px">Tipo de conta:</Text>
            <Input
              value={
                consult.dadosBancario.meioPagamento === "01"
                  ? "Cartão Magnético"
                  : consult.dadosBancario.meioPagamento === "02"
                  ? "Conta Corrente"
                  : ""
              }
              isReadOnly
            />
          </Box>
        </Grid>
      </Grid>
      <Grid
        borderRadius={"8"}
        mb="15px"
        p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
        bg="#fff"
        templateColumns="1fr 1fr 1fr 1fr"
        gap="16px"
        w="100%"
      >
        <Box gridArea="auto / span 4">
          <Text fontWeight="semibold" fontSize="20">
            Margens
          </Text>
          <hr style={{ margin: "10px 0 0px 0" }} />
        </Box>
        <Box>
          <Text mb="8px">Base de cálculo:</Text>
          <Input
            value={
              typeof consult?.dadosMargens?.baseCalculo === "number"
                ? currencyMask(consult?.dadosMargens?.baseCalculo.toFixed(2))
                : consult?.dadosMargens?.baseCalculo ?? ""
            }
            isReadOnly
          />
        </Box>
        <Box>
          <Text mb="8px">MR:</Text>
          <Input
            value={
              typeof consult?.dadosMargens.valorBeneficio === "number"
                ? `R$ ${currencyMask(
                    consult?.dadosMargens.valorBeneficio.toFixed(2)
                  )}`
                : ""
            }
            isReadOnly
          />
        </Box>
        {/* <Box>
          <Text mb="8px">Competência:</Text>
          <Input
            value={
              typeof consult?.dadosMargens.competencia === "string"
                ? consult?.beneficio.margem.competencia
                : ""
            }
            isReadOnly
          />
        </Box> */}
        <Box>
          <Text mb="8px">Possui cartão RMC:</Text>
          <Input
            value={consult?.emprestimosRmc?.length ? "SIM" : "NÃO"}
            isReadOnly
          />
        </Box>
        <Box>
          <Text mb="8px">Possui cartão RCC:</Text>
          <Input
            value={consult?.emprestimosRcc?.length ? "SIM" : "NÃO"}
            isReadOnly
          />
        </Box>
        <Grid
          gridArea={"auto / span 4"}
          templateColumns="1fr 1fr 1fr 1fr"
          gap="16px"
        >
          <Box>
            <Text mb="8px">Quantidade de empréstimos:</Text>
            <Input value={consult?.emprestimos?.length} isReadOnly />
          </Box>
          {/* <Box>
                <Text mb="8px">Cartão Benefício:</Text>
                <Input
                  value={consultLista?.beneficio.margem.cart}
                  isReadOnly
                />
              </Box> */}
          <Box>
            <Text mb="8px">Margem livre 35%</Text>
            <Input
              value={
                typeof consult?.dadosMargens.margemDisponivelEmprestimo ===
                  "number"
                  ? `${consult.dadosMargens.margemDisponivelEmprestimo < 0
                    ? "-"
                    : ""
                  }R$ ${currencyMask(
                    Math.abs(
                      consult.dadosMargens.margemDisponivelEmprestimo
                    ).toFixed(2)
                  )}`
                  : ""
              }
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">Margem cartão RMC:</Text>
            <Input
              value={
                typeof consult?.dadosMargens.margemDisponivelRmc === "number"
                  ? `R$ ${currencyMask(
                      consult?.dadosMargens.margemDisponivelRmc.toFixed(2)
                    )}`
                  : ""
              }
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">Margem cartão RCC:</Text>
            <Input
              value={
                typeof consult?.dadosMargens.margemDisponivelRcc === "number"
                  ? `R$ ${currencyMask(
                      consult?.dadosMargens.margemDisponivelRcc.toFixed(2)
                    )}`
                  : ""
              }
              isReadOnly
            />
          </Box>
        </Grid>
        <Grid
          gridArea={"auto / span 3"}
          templateColumns="1fr 1fr 1fr 1fr"
          gap="16px"
        >
          {/* <Box>
            <Text mb="8px">% Disponível empréstimo:</Text>
            <Input
              // value={
              //   typeof consult?.beneficio.margem
              //     .percentualMargemDisponivelEmprestimo === "number"
              //     ? `${consult?.beneficio.margem.percentualMargemDisponivelEmprestimo}%`
              //     : ""
              // }
              
              isReadOnly
            />
          </Box> */}
          {/* <Box>
            <Text mb="8px">% Disponível cartão:</Text>
            <Input
              value={
                typeof consult?.beneficio.margem
                  .percentualmargemDisponivelCartao === "number"
                  ? `${consult?.beneficio.margem.percentualmargemDisponivelCartao}%`
                  : ""
              }
              isReadOnly
            />
          </Box> */}
          {/* <Box>
            <Text mb="8px">% Total disponível empréstimo:</Text>
            <Input
              value={
                typeof consult?.beneficio.margem
                  .percentualMargemTotalEmprestimo === "number"
                  ? `${consult?.beneficio.margem.percentualMargemTotalEmprestimo}%`
                  : ""
              }
              isReadOnly
            />
          </Box>
          <Box>
            <Text mb="8px">% Total disponível cartão:</Text>
            <Input
              value={
                typeof consult?.beneficio.margem.percentualMargemTotalCartao ===
                "number"
                  ? `${consult?.beneficio.margem.percentualMargemTotalCartao}%`
                  : ""
              }
              isReadOnly
            />
          </Box> */}
        </Grid>
      </Grid>
      <Grid
        borderRadius={"8"}
        mb="15px"
        p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
        bg="#fff"
        templateColumns="1fr 1fr 1fr"
        gap="16px"
        w="100%"
      >
        <Box gridArea="auto / span 3">
          <Text fontWeight="semibold" fontSize="20">
            Contrato Cartão RMC
          </Text>
          <hr style={{ margin: "10px 0 0px 0" }} />
        </Box>
        <Box gridArea="auto / span 3">
          <DynamicTable
            columns={contratoCartaoColumns}
            rows={consult?.emprestimosRmc || []}
            containerStyle={{
              boxShadow: "0 0 1em rgba(0,0,0, 0.03)",
            }}
          />
        </Box>
      </Grid>
      <Grid
        borderRadius={"8"}
        mb="15px"
        p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
        bg="#fff"
        templateColumns="1fr 1fr 1fr"
        gap="16px"
        w="100%"
      >
        <Box gridArea="auto / span 3">
          <Text fontWeight="semibold" fontSize="20">
            Contrato Cartão RCC
          </Text>
          <hr style={{ margin: "10px 0 0px 0" }} />
        </Box>
        <Box gridArea="auto / span 3">
          <DynamicTable
            columns={contratoCartaoColumns}
            rows={consult?.emprestimosRcc || []}
            containerStyle={{
              boxShadow: "0 0 1em rgba(0,0,0, 0.03)",
            }}
          />
        </Box>
      </Grid>
      <Grid
        borderRadius={"8"}
        mb="15px"
        p={consult ? "10px 20px 15px 20px" : "10px 20px 10px 20px"}
        bg="#fff"
        templateColumns="1fr 1fr 1fr"
        gap="16px"
        w="100%"
      >
        <Box gridArea="auto / span 3">
          <Text fontWeight="semibold" fontSize="20">
            Contratos
          </Text>
          <hr style={{ margin: "10px 0 0px 0" }} />
        </Box>
        <Box gridArea="auto / span 3">
          <DynamicTable
            columns={contratosBancariosColumns}
            rows={consult?.emprestimos || []}
            containerStyle={{
              boxShadow: "0 0 1em rgba(0,0,0, 0.03)",
            }}
          />
        </Box>
      </Grid>
      <ConsultaDetalhamentoModal consult={consult} />
      <ConsultaPDFModal consult={consult} />
    </Box>
  );
}
